import {
  ArrowDown,
  ArrowUp,
  Clock,
  Edit,
  Image,
  Image as ImageIcon,
  Loader2,
  MoreHorizontal,
  Music,
  Plus,
  Search,
  Trash,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useAddStudentsToPerformanceMutation } from '~entities/recital-manager/api/add-students-to-performance';
import { useDeletePerformanceMutation } from '~entities/recital-manager/api/delete-performance';
import { useGetAllPerformances } from '~entities/recital-manager/api/get-all-performances';
import { useRemoveStudentFromPerformanceMutation } from '~entities/recital-manager/api/remove-student-from-performance';
import { useReorderPerformanceMutation } from '~entities/recital-manager/api/reorder-performance';
import type { Performance } from '~entities/recital-manager/model/recital';
import { useGetAllStudentsWithSearch } from '~entities/student';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import { CostumeImagePreviewDialog } from '~features/recital-manager/ui/costume-image-preview-dialog';
import { CreatePerformance } from '~features/recital-manager/ui/create-performance';
import { EditPerformanceSheet } from '~features/recital-manager/ui/edit-performance-sheet';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/shared/shadcn/ui/alert-dialog';
import { Badge } from '~/shared/shadcn/ui/badge';
import { Button } from '~/shared/shadcn/ui/button';
import { Checkbox } from '~/shared/shadcn/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/shared/shadcn/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/shared/shadcn/ui/dropdown-menu';
import { Input } from '~/shared/shadcn/ui/input';
import { ScrollArea } from '~/shared/shadcn/ui/scroll-area';
import useDebounce from '~shared/hooks/useDebounce';

import { UploadCostumeImage } from './upload-costume-image';
import { UploadMusicFile } from './upload-music-file';

interface PerformancesListProps {
  actId: string;
  recitalId: string;
}

export const PerformancesList = ({ actId }: PerformancesListProps) => {
  const { hasAdminAccess } = useCurrentUserRole();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  // Edit now handled by EditPerformanceSheet component
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [uploadMusicDialogOpen, setUploadMusicDialogOpen] = useState(false);
  const [uploadCostumeDialogOpen, setUploadCostumeDialogOpen] = useState(false);
  const [manageStudentsDialogOpen, setManageStudentsDialogOpen] =
    useState(false);
  const [selectedPerformance, setSelectedPerformance] =
    useState<Performance | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudentIds, setSelectedStudentIds] = useState<Array<string>>(
    [],
  );
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const { data: performancesData, isFetching: isFetchingPerformances } =
    useGetAllPerformances({
      actId,
    });

  const deletePerformanceMutation = useDeletePerformanceMutation();
  const {
    mutateAsync: reorderPerformance,
    isPending: isReorderingPerformance,
  } = useReorderPerformanceMutation();
  const addStudentsMutation = useAddStudentsToPerformanceMutation();
  const removeStudentMutation = useRemoveStudentFromPerformanceMutation();

  const { data: studentsData, isLoading: isLoadingStudents } =
    useGetAllStudentsWithSearch({
      page: 1,
      limit: 50,
      name: debouncedSearchTerm,
    });

  const handleDelete = () => {
    if (!selectedPerformance) return;

    deletePerformanceMutation.mutate(
      { actId, performanceId: selectedPerformance._id },
      {
        onSuccess: () => {
          toast.success('Performance deleted successfully!');
          setDeleteDialogOpen(false);
          setSelectedPerformance(null);
        },
        onError: (error: any) => {
          const errorMessage =
            error.response?.data?.error?.message ||
            error.response?.data?.message ||
            'Failed to delete performance';
          toast.error(errorMessage);
        },
      },
    );
  };

  const handleReorder = async (
    performanceId: string,
    direction: 'up' | 'down',
  ) => {
    const performances = performancesData ?? [];
    const performance = performances.find((p) => p._id === performanceId);
    if (!performance) return;

    // Use the actual orderIndex from the performance, not the array position
    const currentOrderIndex = performance.orderIndex;
    const newOrderIndex =
      direction === 'up' ? currentOrderIndex - 1 : currentOrderIndex + 1;

    // Validate bounds
    if (newOrderIndex < 0 || newOrderIndex >= performances.length) {
      return;
    }

    try {
      await reorderPerformance({
        actId,
        performanceId,
        orderIndex: newOrderIndex,
      });
      toast.success('Performance reordered successfully!');
    } catch {
      toast.error('Failed to reorder performance');
    }
  };

  const openDeleteDialog = (performance: Performance) => {
    setSelectedPerformance(performance);
    setDeleteDialogOpen(true);
  };

  const openUploadMusicDialog = (performance: Performance) => {
    setSelectedPerformance(performance);
    setUploadMusicDialogOpen(true);
  };

  const openUploadCostumeDialog = (performance: Performance) => {
    setSelectedPerformance(performance);
    setUploadCostumeDialogOpen(true);
  };

  const openManageStudentsDialog = (performance: Performance) => {
    setSelectedPerformance(performance);
    setSelectedStudentIds(performance.studentIds || []);
    setSearchTerm('');
    setManageStudentsDialogOpen(true);
  };

  const handleToggleStudent = (studentId: string) => {
    setSelectedStudentIds((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId],
    );
  };

  const handleSaveStudents = async () => {
    if (!selectedPerformance) return;

    const currentStudentIds = selectedPerformance.studentIds ?? [];
    const studentsToAdd = selectedStudentIds.filter(
      (id) => !currentStudentIds.includes(id),
    );
    const studentsToRemove = currentStudentIds.filter(
      (id) => !selectedStudentIds.includes(id),
    );

    if (studentsToAdd.length === 0 && studentsToRemove.length === 0) {
      setManageStudentsDialogOpen(false);
      return;
    }

    try {
      if (studentsToAdd.length > 0) {
        await addStudentsMutation.mutateAsync({
          actId,
          performanceId: selectedPerformance._id,
          studentIds: studentsToAdd,
        });
      }

      // Run removals sequentially to avoid overlapping mutation state.
      for (const studentId of studentsToRemove) {
        await removeStudentMutation.mutateAsync({
          actId,
          performanceId: selectedPerformance._id,
          studentId,
        });
      }

      toast.success('Students updated successfully!');
      setManageStudentsDialogOpen(false);
      setSelectedPerformance(null);
      setSearchTerm('');
    } catch (error: any) {
      toast.error(error.response?.data?.message ?? 'Failed to update students');
    }
  };

  const performances = performancesData ?? [];

  const renderCostumeThumbnail = (
    performance: Performance,
    imageClassName: string,
    placeholderClassName: string,
  ) => {
    if (performance.costume?.imageUrl) {
      return (
        <CostumeImagePreviewDialog
          imageUrl={performance.costume.imageUrl}
          performanceName={performance.name}
        >
          <img
            src={performance.costume.imageUrl}
            alt='Costume Image'
            className={`cursor-pointer rounded-md object-cover transition-opacity hover:opacity-80 ${imageClassName}`}
          />
        </CostumeImagePreviewDialog>
      );
    }

    return (
      <div
        className={`flex items-center justify-center rounded-md bg-muted text-muted-foreground ${placeholderClassName}`}
      >
        <Image />
      </div>
    );
  };

  const renderPerformanceMetaBadges = (performance: Performance) => (
    <>
      {performance.studentIds.length > 0 ? (
        <Badge variant='secondary'>
          <Users className='mr-1 size-3 shrink-0' />
          {performance.studentIds.length}{' '}
          {performance.studentIds.length === 1 ? 'student' : 'students'}
        </Badge>
      ) : null}
      {performance.musicFile ? (
        <Badge variant='default' className='gap-1'>
          <Music className='size-3' />
          Music
        </Badge>
      ) : null}
      <Badge variant='outline'>
        <Clock className='mr-1 size-3 shrink-0' />
        {performance.duration} min
      </Badge>
    </>
  );

  const renderPerformanceActionsMenu = (performance: Performance) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant='ghost' size='sm' className='shrink-0'>
          <MoreHorizontal className='size-4' />
          <span className='sr-only'>Performance actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end'>
        <EditPerformanceSheet actId={actId} performance={performance}>
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
            }}
          >
            <Edit className='mr-2 size-4' />
            Edit Performance
          </DropdownMenuItem>
        </EditPerformanceSheet>
        <DropdownMenuItem
          onClick={() => {
            openUploadMusicDialog(performance);
          }}
        >
          <Music className='mr-2 size-4' />
          {performance.musicFile ? 'Update Music' : 'Upload Music'}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            openUploadCostumeDialog(performance);
          }}
        >
          <ImageIcon className='mr-2 size-4' />
          Upload Costume Image
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            openManageStudentsDialog(performance);
          }}
        >
          <Users className='mr-2 size-4' />
          Manage Students
        </DropdownMenuItem>
        {hasAdminAccess ? (
          <DropdownMenuItem
            onClick={() => {
              openDeleteDialog(performance);
            }}
            className='text-red-600'
          >
            <Trash className='mr-2 size-4' />
            Delete Performance
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div>
      <div className='mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <h3 className='text-lg font-semibold'>Performances</h3>
        {hasAdminAccess && (
          <Button
            size='sm'
            className='w-full sm:w-auto'
            onClick={() => {
              setCreateDialogOpen(true);
            }}
          >
            <Plus />
            Add Performance
          </Button>
        )}
      </div>

      {isFetchingPerformances ? (
        <div className='flex items-center justify-center py-4'>
          <Loader2 className='size-6 animate-spin' />
        </div>
      ) : performances.length === 0 ? (
        <div className='rounded-lg border-2 border-dashed py-8 text-center text-sm text-muted-foreground'>
          No performances yet. Click &quot;Add Performance&quot; to create one.
        </div>
      ) : (
        <div className='space-y-3'>
          {performances.map((performance, index) => (
            <div
              key={performance._id}
              className='rounded-lg border bg-card p-4 transition-colors hover:bg-muted/50'
            >
              {/* Mobile */}
              <div className='flex flex-col gap-3 sm:hidden'>
                <div className='flex items-start gap-2'>
                  <Badge
                    variant='outline'
                    className='shrink-0 font-mono text-xs tabular-nums'
                  >
                    #{index + 1}
                  </Badge>
                  <p className='min-w-0 flex-1 text-base font-medium capitalize leading-snug'>
                    {performance.name}
                  </p>
                  {renderPerformanceActionsMenu(performance)}
                </div>

                <div className='flex items-start gap-3'>
                  {renderCostumeThumbnail(
                    performance,
                    'size-14 shrink-0',
                    'size-14 shrink-0',
                  )}
                  <div className='flex min-w-0 flex-1 flex-wrap gap-2'>
                    {renderPerformanceMetaBadges(performance)}
                  </div>
                </div>

                {performance.description ? (
                  <p className='text-sm leading-relaxed text-muted-foreground'>
                    {performance.description}
                  </p>
                ) : null}

                <div className='flex gap-2 border-t pt-3'>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    className='flex-1'
                    disabled={index === 0 || isReorderingPerformance}
                    onClick={() => {
                      void handleReorder(performance._id, 'up');
                    }}
                  >
                    <ArrowUp className='mr-1 size-4' />
                    Move up
                  </Button>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    className='flex-1'
                    disabled={
                      index === performances.length - 1 ||
                      isReorderingPerformance
                    }
                    onClick={() => {
                      void handleReorder(performance._id, 'down');
                    }}
                  >
                    <ArrowDown className='mr-1 size-4' />
                    Move down
                  </Button>
                </div>
              </div>

              {/* Desktop */}
              <div className='hidden items-center gap-3 sm:flex'>
                <div className='flex shrink-0 flex-col gap-1'>
                  <Button
                    variant='ghost'
                    size='sm'
                    className='size-6 p-0'
                    onClick={() => {
                      void handleReorder(performance._id, 'up');
                    }}
                    disabled={index === 0 || isReorderingPerformance}
                  >
                    <ArrowUp className='size-3' />
                  </Button>
                  <Button
                    variant='ghost'
                    size='sm'
                    className='size-6 p-0'
                    onClick={() => {
                      void handleReorder(performance._id, 'down');
                    }}
                    disabled={
                      index === performances.length - 1 ||
                      isReorderingPerformance
                    }
                  >
                    <ArrowDown className='size-3' />
                  </Button>
                </div>

                <span className='shrink-0 font-mono text-sm text-muted-foreground'>
                  #{index + 1}
                </span>

                {renderCostumeThumbnail(
                  performance,
                  'size-10 shrink-0',
                  'size-10 shrink-0',
                )}

                <div className='min-w-0 flex-1'>
                  <div className='flex items-start justify-between gap-2'>
                    <span className='font-medium capitalize'>
                      {performance.name}
                    </span>
                    {renderPerformanceActionsMenu(performance)}
                  </div>
                  <div className='mt-2 flex flex-wrap items-center gap-1.5'>
                    {renderPerformanceMetaBadges(performance)}
                  </div>
                  {performance.description ? (
                    <p className='mt-2 text-sm text-muted-foreground'>
                      {performance.description}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Performance Dialog */}
      <CreatePerformance
        actId={actId}
        orderIndex={performancesData?.length ?? 0}
        createDialogOpen={createDialogOpen}
        setCreateDialogOpen={setCreateDialogOpen}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Performance</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{selectedPerformance?.name}
              &quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className='bg-red-600 hover:bg-red-700'
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <UploadMusicFile
        actId={actId}
        open={uploadMusicDialogOpen}
        setOpen={setUploadMusicDialogOpen}
        performance={selectedPerformance}
        setSelectedPerformance={setSelectedPerformance}
      />

      {/* Upload Costume Image Dialog */}
      <UploadCostumeImage
        actId={actId}
        uploadCostumeDialogOpen={uploadCostumeDialogOpen}
        setUploadCostumeDialogOpen={setUploadCostumeDialogOpen}
        selectedPerformance={selectedPerformance}
        setSelectedPerformance={setSelectedPerformance}
      />

      {/* Manage Students Dialog */}
      <Dialog
        open={manageStudentsDialogOpen}
        onOpenChange={setManageStudentsDialogOpen}
      >
        <DialogContent className='max-w-2xl'>
          <DialogHeader>
            <DialogTitle>Manage Students</DialogTitle>
            <DialogDescription>
              Select students for &quot;{selectedPerformance?.name}&quot;
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-4'>
            <div className='relative'>
              <Search className='absolute left-2 top-2.5 size-4 text-muted-foreground' />
              <Input
                placeholder='Search students...'
                className='pl-8'
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                }}
              />
            </div>

            <div className='flex items-center justify-between'>
              <span className='text-sm text-muted-foreground'>
                {selectedStudentIds.length} students selected
              </span>
              {selectedStudentIds.length > 0 && (
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => {
                    setSelectedStudentIds([]);
                  }}
                >
                  Clear all
                </Button>
              )}
            </div>

            <ScrollArea className='h-[400px] rounded-md border'>
              <div className='p-2'>
                {isLoadingStudents ? (
                  <div className='flex items-center justify-center py-8'>
                    <div className='text-sm text-muted-foreground'>
                      Loading students...
                    </div>
                  </div>
                ) : studentsData?.data.students.length === 0 ? (
                  <div className='py-8 text-center text-sm text-muted-foreground'>
                    No students found
                  </div>
                ) : (
                  studentsData?.data.students.map((student) => (
                    <div
                      key={student._id}
                      className='flex cursor-pointer items-center space-x-3 rounded-md p-2 hover:bg-muted'
                      onClick={() => {
                        handleToggleStudent(student._id);
                      }}
                    >
                      <Checkbox
                        checked={selectedStudentIds.includes(student._id)}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        onCheckedChange={() => {
                          handleToggleStudent(student._id);
                        }}
                      />
                      <div className='flex-1'>
                        <p className='text-sm font-medium'>{`${student.firstName} ${student.lastName}`}</p>
                        {student.familyName && (
                          <p className='text-xs text-muted-foreground'>
                            {student.familyName}
                          </p>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => {
                setManageStudentsDialogOpen(false);
                setSelectedPerformance(null);
                setSearchTerm('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveStudents}
              disabled={
                addStudentsMutation.isPending || removeStudentMutation.isPending
              }
            >
              {addStudentsMutation.isPending || removeStudentMutation.isPending
                ? 'Saving...'
                : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
